import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import debounce from 'lodash.debounce'
import { BasicSlider } from '../BasicSlider/BasicSlider'
import { PriceBox } from '../PriceBox/PriceBox'
import { Button } from '../Button/Button'
import { useMatchMedia } from '../../features/useMatchMedia'
import deprecated from '../../utils/deprecated'

/**
 * Bronson CompareAndSelect component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-comapre-and-select.html
 *
 * @param {boolean} [buttonVariant=false] - Check the component as selected via the internal button.
 * @param {CompareAndSelectItem} [children] - A collection of items.
 * @param {number} [defaultSelected] - The default select item by its zero-based index.
 * @param {boolean} [deselectable] - Allow deselecting items.
 * @param {string} [inputGroup] - Value for the group name (`[name]`) of all {@link CompareAndSelectItem}.
 * @param {function} [onChange] - Callback to handle global items changes and update internal selection state.
 * @param {string} [radioButtonSection] - A flag to signalize that the component utilizes radio buttons for selection tracking.
 * @param {boolean} [sliderCounter] - Replace slider dots with counter for pagination, e.g. 1/4.
 * @param {boolean} [stacked] - Render the `stacked` variant.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The CompareAndSelect component.
 * @constructor
 */
export function CompareAndSelect({
  buttonVariant = false,
  children,
  defaultSelected,
  deselectable,
  inputGroup,
  onChange,
  radioButtonSection,
  sliderCounter,
  stacked,
  testId,
  ...otherProps
}) {
  const [flickityRef, setFlickityRef] = useState(null)
  const [selected, setSelected] = useState(defaultSelected)
  const initialResizeThreshold = 250

  const compareSelectAccordionBreakpointCustomProp = '--bron-compare-select-accordion-breakpoint'
  const [breakpoint, setBreakpoint] = useState(null)
  const matchBreakpoint = useMatchMedia(`(min-width: ${breakpoint})`)
  const stackedElementRef = useRef(null)

  /**
   * Handles {@link flickityRef} and {@link stackedElementRef} depending on the
   * current breakpoint when {@link stacked}.
   * Get the current breakpoint for {@link compareSelectAccordionBreakpointCustomProp}
   * and set it as {@link breakpoint}.
   */
  useEffect(() => {
    const refElement = matchBreakpoint ? flickityRef?.element : stackedElementRef?.current

    /**
     * Cleanup function to prevent the internal state of the component
     * from being updated when it is unmounted.
     */
    const timer = setTimeout(() => {
      if (refElement && !breakpoint) {
        const currentBreakpoint = getComputedStyle(refElement).getPropertyValue(
          compareSelectAccordionBreakpointCustomProp
        )
        setBreakpoint(currentBreakpoint)
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [flickityRef, stackedElementRef, breakpoint, matchBreakpoint])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && deselectable && radioButtonSection) {
      throw new Error('A radio button group cannot be deselectable.')
    }
  }, [deselectable, radioButtonSection])

  const updateSelected = (newSelected) => {
    if (newSelected === selected) {
      if (deselectable && !radioButtonSection) {
        // eslint-disable-next-line no-param-reassign
        newSelected = null
      }
    }
    setSelected(newSelected)
    if (onChange) {
      onChange(newSelected)
    }
    flickityRef && flickityRef.resize()
  }

  const toggleSliderControls = useCallback(() => {
    if (flickityRef) {
      const size = flickityRef.size.width
      const { slideableWidth } = flickityRef
      const slideable = slideableWidth > size

      const pageDotsHTMLCollection = flickityRef.element.getElementsByClassName('flickity-page-dots')
      const flickityButtonsHTMLCollection = flickityRef.element.getElementsByClassName('flickity-prev-next-button')

      /**
       * This fixes a bug where Flickity is unable to reassign the draggable behavior when resizing from a big to small
       * viewport when `groupCells` or `contain` options are set.
       * The fix only kicks in when the `draggable` option {@link https://flickity.metafizzy.co/options.html#draggable} in Flickity was set.
       * @see https://github.com/metafizzy/flickity/issues/960
       */
      if (flickityRef.options.draggable) {
        flickityRef.isDraggable = slideable
        flickityRef.element.classList.toggle('is-draggable', slideable)
      }

      Array.from(pageDotsHTMLCollection).forEach((pageDots) => {
        pageDots.classList.toggle('is-slideable', slideable)
      })

      Array.from(flickityButtonsHTMLCollection).forEach((btn) => {
        btn.classList.toggle('is-slideable', slideable)
      })
    }
  }, [flickityRef])

  useEffect(() => {
    const instance = flickityRef
    if (instance) {
      const resizeFn = instance.resize.bind(instance)
      instance.resize = () => {
        instance.element.classList.remove('flickity-resize')
        resizeFn()
        requestAnimationFrame(() => {
          instance.element.classList.add('flickity-resize')
          /**
           * Always keep the selected items visible.
           */
          instance.selectCell(selected, false, true)
        })
      }
      const onReady = () => {
        debounce(instance.resize, initialResizeThreshold)()
        /**
         * Fixes nasty layouting bug for some journeys.
         * The {@link initialResizeThreshold} must be increased for this to work.
         * @see https://jira.platform.vwfs.io/browse/BRON-7534
         */
        const timeout = setTimeout(() => {
          resizeFn()
        }, initialResizeThreshold + 50)
        return () => {
          timeout && clearTimeout(timeout)
        }
      }

      instance.on('select', toggleSliderControls)
      instance.on('ready', onReady)
      return () => {
        instance.off('select', toggleSliderControls)
        instance.off('ready', onReady)
      }
    }
    return () => {}
  }, [flickityRef, selected, toggleSliderControls])

  useEffect(() => {
    if (flickityRef) {
      /**
       * Resize Flickity and recalculate the position of all children.
       * @see https://flickity.metafizzy.co/api.html#reloadcells
       * @see https://flickity.metafizzy.co/api.html#resize
       */
      flickityRef.reloadCells()
      flickityRef.resize()
    }
  }, [flickityRef, children, defaultSelected])

  const flickityOptions = {
    cellSelector: '.js-compare-and-select__item',
    cellAlign: 'center',
    groupCells: '100%',
    initialIndex: defaultSelected,
  }

  const flickityClassNameList = classNames(
    'c-compare-and-select-container__inner',
    'js-compare-and-select',
    'is-initialized',
    {
      'c-compare-and-select--stacked': stacked,
    }
  ).trim()

  /* Use 'CompareAndSelect.Item' component. */
  const compareAndSelectItems = () =>
    React.Children.map(children, (child, index) => {
      if (!child) {
        return null
      }
      return React.cloneElement(child, {
        selected: selected === index,
        updateSelected,
        index,
        buttonVariant,
        radioButtonSection,
        inputGroup,
        stacked,
        matchBreakpoint,
      })
    })

  function renderStacked() {
    return (
      <div className="c-compare-and-select-container__inner  c-compare-and-select--stacked" ref={stackedElementRef}>
        {compareAndSelectItems()}
      </div>
    )
  }

  function renderSlider() {
    return (
      <BasicSlider
        elementType="div"
        className={flickityClassNameList}
        flickityRef={(c) => setFlickityRef(c)}
        options={flickityOptions}
        sliderCounter={sliderCounter}
      >
        {compareAndSelectItems()}
      </BasicSlider>
    )
  }

  return (
    <div {...otherProps} data-testid={testId} className="c-compare-and-select-container">
      {
        /**
         * Mounts {@link BasicSlider} only if the media query
         * in {@link matchBreakpoint} matches when {@link stacked}.
         */
        stacked && !matchBreakpoint ? renderStacked() : renderSlider()
      }
    </div>
  )
}

CompareAndSelect.propTypes = {
  buttonVariant: PropTypes.bool, // Bronson template: 'is-button-variant'.
  children: PropTypes.node, // Bronson template: 'compare-and-select'. Use 'CompareAndSelect.Item' component.
  defaultSelected: PropTypes.number, // the default selected item by index.
  deselectable: PropTypes.bool, // if set 'true' items can be deselected, default: false
  inputGroup: PropTypes.string.isRequired, // Bronson template: 'input-group'.
  onChange: PropTypes.func, // onChange callback when selection changes.
  radioButtonSection: PropTypes.bool, // Bronson template: 'has-radio-button-section'.
  sliderCounter: PropTypes.bool, // Bronson template: 'data-slider-counter'
  stacked: PropTypes.bool, // Bronson template: 'is-stacked'.
  testId: PropTypes.string, // Added for data-testid attribute.
}

/**
 * CompareAndSelectItem
 * Render a single {@link CompareAndSelect} children item.
 * @internal
 *
 * @param {string} [ariaLabelDiscountOldPrice] - See {@link PriceBox.ariaLabelDiscountOldPrice}.
 * @param {string} [ariaLabelDiscountValue] - See {@link PriceBox.ariaLabelDiscountValue}.
 * @param {string} [ariaLabelPrice] - See {@link PriceBox.ariaLabelPrice}.
 * @param {string} [buttonLabel] - Button label for the call-to-action.
 * @param {boolean} [buttonVariant] - Check the component as selected via the internal `button`.
 * @param {React.ReactNode} [checkbox] - Display a `checkbox`, e.g. for legal terms.
 * @param {CompareAndSelect.IconListItem} [itemChildren] - A collection of {@link CompareAndSelect.IconListItem} as children.
 * @param {string} [className] - Additional CSS classes to append on the component element.
 * @param {React.ReactNode} [description] - Render the description section.
 * @param {string} [descriptionClassName] - Additional CSS classes to append on the description element.
 * @param {InfoIcon} [descriptionInfoIcon] - Render an info icon inside the description element.
 * @param {string} [descriptionLinkHref] - Additional `[href]` value on the description element.
 * @param {string} [descriptionLinkLabel] - Additional link text on the description element.
 * @param {string} [descriptionTitle] - A small heading to render on the description element.
 * @param {string} [detailsButtonLabel] - Label for the button to expand the {@link stacked} variant.
 * @param {boolean} [disableButton] - Disable the selection button.
 * @param {React.ReactNode} [discountOldPrice] - See {@link PriceBox.discountOldPrice}.
 * @param {React.ReactNode} [discountValue] - See {@link PriceBox.discountValue}
 * @param {boolean} [flexibleButton] - Render the call-to-action button directly below the price section.
 * @param {string} [icon] - Name of the icon to render in the header section.
 * @param {number} index - The index number for this item.
 *                         Implicitly passed via `cloneElement` inside {@link CompareAndSelect} parent.
 * @param {string} [indicatorTitle] - Content for the indicator title. If {@link indicatorTitleAttr} is null, also rendered as content for the `[title]` attribute value.
 * @param {null|boolean|string} [indicatorTitleAttr=null] - Custom `[title]` attribute value on the indicator title element.
 * @param {InfoIcon} [infoIcon] - Pass in the {@link InfoIcon} into the header. Still used for backward compatibility.
 * @param {string} [inputAttributes] - Additional attributes on the `radio` input element.
 * @param {string} inputGroup - Value for the group name (`[name]`).
 *                              Implicitly passed via `cloneElement` inside {@link CompareAndSelect} parent.
 * @param {string} [label] - Render content for the `.c-compare-and-select__label`.
 * @param {string} [legal] - Render a legal section.
 * @param {string} [legalTitle] - Title content for the {@link legal} section.
 * @param {string} [matchBreakpoint] - Flag to signalize if the {@link stacked} variant of the component is expanded or not.
 *                                     Implicitly passed via `cloneElement` inside {@link CompareAndSelect} parent.
 * @param {string} [price] - See {@link PriceBox.price}.
 * @param {string} [pricingAdditionalInfo] - See {@link PriceBox.pricingAdditionalInfo}.
 * @param {string} [pricingHeader] - See {@link PriceBox.header}.
 * @param {string} [radioButtonSection] - Flag to signalize that the component utilizes radio buttons for selection tracking.
 * @param {string} [radioDescription] - The {@link radioButtonSection} label.
 * @param {InfoIcon} [radioInfoIcon] - Render an {@link InfoIcon} inside the {@link radioButtonSection}.
 * @param {string} [radioValue] - Content to display before the {@link radioDescription}.
 * @param {boolean} [selected] - Selection status of the current item.
 *                               Implicitly passed via `cloneElement` inside {@link CompareAndSelect} parent.
 * @param {string} [stacked] - Render the `stacked` variant.
 *                             Implicitly passed via `cloneElement` inside {@link CompareAndSelect} parent.
 * @param {string} [title] - Content for the title. If {@link titleAttr} is null, also rendered as content for the `[title]` attribute value.
 * @param {null|boolean|string} [titleAttr=null] - Custom `[title]` attribute value on the title element.
 * @param {boolean} [truncateTitle] - Truncate the title with ellipsis.
 * @param {function} [updateSelected] - Callback to call upon `onClick` on the call-to-action. Gets the current {@link index} passed as an argument.
 *                                      Implicitly passed via `cloneElement` inside {@link CompareAndSelect} parent.
 * @param {string} [value] - Deprecated, please use "price" instead.
 * @param {string} [valueSuffix] - Deprecated, please use `pricingHeader` instead.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The CompareAndSelectItem component.
 * @constructor
 */
export function CompareAndSelectItem({
  ariaLabelDiscountOldPrice,
  ariaLabelDiscountValue,
  ariaLabelPrice,
  buttonLabel,
  buttonVariant,
  checkbox,
  children: itemChildren,
  className,
  description,
  descriptionClassName,
  descriptionInfoIcon,
  descriptionLinkHref,
  descriptionLinkLabel,
  descriptionTitle,
  detailsButtonLabel,
  disableButton,
  discountOldPrice,
  discountValue,
  flexibleButton,
  icon,
  index,
  indicatorTitle,
  indicatorTitleAttr = null,
  infoIcon,
  inputAttributes,
  inputGroup,
  label,
  legal,
  legalTitle,
  matchBreakpoint,
  price,
  pricingAdditionalInfo,
  pricingHeader,
  radioButtonSection,
  radioDescription,
  radioInfoIcon,
  radioValue,
  selected,
  stacked,
  testId,
  title,
  titleAttr = null,
  truncateTitle,
  updateSelected,
  value,
  valueSuffix,
  ...otherProps
}) {
  const priceValue = value || price
  const priceHeader = (
    <>
      {valueSuffix || pricingHeader}
      {infoIcon}
    </>
  )

  const itemClassNameList = classNames('c-compare-and-select__item', 'js-compare-and-select__item', className).trim()

  const inputClassNameList = classNames('c-compare-and-select__hidden-input', {
    'c-radio__input': radioButtonSection,
  }).trim()

  const divClassNameList = classNames('c-compare-and-select', {
    'c-compare-and-select--selected': buttonVariant && selected,
    'c-radio__label-container': radioButtonSection,
  }).trim()

  const iClassNameList = classNames({
    'c-compare-and-select__icon': icon,
    'c-icon': icon,
    [`c-icon--[${icon}]`]: icon,
  }).trim()

  const descriptionClassNameList = classNames('c-compare-and-select__description', descriptionClassName).trim()

  const titleTextClassNameList = classNames('c-compare-and-select__title-text', {
    'u-text-truncate': truncateTitle,
  }).trim()

  const renderCompareAndSelectButton = () => (
    <Button
      onClick={() => updateSelected(index)}
      className="c-compare-and-select__button"
      type="button"
      disabled={disableButton}
    >
      {buttonLabel}
    </Button>
  )

  const renderCompareAndSelectLabel = () => (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <label
        className="c-compare-and-select__button c-btn"
        htmlFor={`${inputGroup}-${index}`}
        onClick={() => updateSelected(index)}
        disabled={disableButton}
      >
        {buttonLabel}
      </label>
    </>
  )

  function renderUnlessButtonVariant() {
    if (!buttonVariant) {
      return (
        <input
          className={inputClassNameList}
          type="radio"
          name={inputGroup}
          id={`${inputGroup}-${index}`}
          checked={selected}
          readOnly
          {...inputAttributes}
        />
      )
    }
    return null
  }

  function renderIfLabel() {
    return <div className="c-compare-and-select__label">{label}</div>
  }

  function renderIfIcon() {
    if (icon) {
      return <i className={iClassNameList} aria-hidden="true" role="img" />
    }
    return null
  }

  function renderIfValue() {
    if (priceValue) {
      return (
        <div className="c-compare-and-select__pricing">
          {/* @TODO: Extract to new prop `priceBox` and deprecate related props. */}
          <PriceBox
            header={priceHeader}
            ariaLabelDiscountValue={ariaLabelDiscountValue || 'Discount:'}
            discountValue={discountValue}
            ariaLabelDiscountOldPrice={ariaLabelDiscountOldPrice || 'Old Price:'}
            discountOldPrice={discountOldPrice}
            ariaLabelPrice={ariaLabelPrice || 'Discount Price:'}
            price={priceValue}
            additionalInfo={pricingAdditionalInfo}
          />
        </div>
      )
    }
    return null
  }

  function renderIfDetails() {
    if (stacked && itemChildren) {
      return (
        <details
          className="c-compare-and-select__details  js-compare-and-select__details"
          open={matchBreakpoint ? true : null}
        >
          <summary className="c-compare-and-select__summary">
            <Button link small simple iconReversed icon="semantic-expand" element="span">
              {detailsButtonLabel}
            </Button>
          </summary>
          {renderIfChildren()}
        </details>
      )
    }
    return null
  }

  function renderIfChildren() {
    if (itemChildren) {
      return (
        <ul className="c-compare-and-select__list c-icon-list c-icon-list--small">
          {itemChildren /* Use 'SelectAndCompare.CompareAndSelect.IconListItem' component. */}
        </ul>
      )
    }
    return null
  }

  function renderIfLinkInDescription() {
    if (descriptionLinkHref && descriptionLinkLabel) {
      return <a href={descriptionLinkHref}>{descriptionLinkLabel}</a>
    }
    return null
  }

  function renderIfDescription() {
    if (description) {
      return (
        <p className={descriptionClassNameList}>
          {descriptionTitle && (
            <>
              <strong>{descriptionTitle}</strong>:{' '}
            </>
          )}
          {description}
          {renderIfLinkInDescription()}
          {descriptionInfoIcon}
        </p>
      )
    }
    return null
  }

  function renderIfButton() {
    if (!flexibleButton && buttonLabel) {
      return <>{buttonVariant ? renderCompareAndSelectButton() : renderCompareAndSelectLabel()}</>
    }
    return null
  }

  function renderIfFlexibleButton() {
    if (flexibleButton && buttonLabel) {
      return <>{buttonVariant ? renderCompareAndSelectButton() : renderCompareAndSelectLabel()}</>
    }
    return null
  }

  function renderIfCheckbox() {
    if (checkbox) {
      return <div className="c-compare-and-select__checkbox">{checkbox}</div>
    }
    return null
  }

  function renderIfLegal() {
    if (legal) {
      return (
        <p className="c-compare-and-select__legal">
          {legalTitle && (
            <>
              <strong>{legalTitle}:</strong>{' '}
            </>
          )}
          {legal}
        </p>
      )
    }
    return null
  }

  function renderIfRadioButtonSection() {
    if (radioButtonSection) {
      return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <label
          className="c-radio c-compare-and-select__radio"
          htmlFor={`${inputGroup}-${index}`}
          onClick={(e) => e.target.className.search('info-icon') === -1 && updateSelected(index)}
        >
          <div className="c-radio__label c-compare-and-select__radio-label" checked={buttonVariant && selected}>
            {radioValue}
            {radioInfoIcon}
            <div className="c-compare-and-select__radio-description">{radioDescription}</div>
          </div>
        </label>
      )
    }
    return null
  }

  function renderIfIndicatorTitle() {
    if (indicatorTitle) {
      return (
        <h3 className="c-compare-and-select__title-indicator-text" title={indicatorTitleAttr ?? indicatorTitle}>
          {indicatorTitle}
        </h3>
      )
    }
    return null
  }

  return (
    <div {...otherProps} className={itemClassNameList} data-testid={testId}>
      {renderUnlessButtonVariant()}
      <div className={divClassNameList}>
        {renderIfLabel()}
        <header className="c-compare-and-select__title">
          {renderIfIcon()}
          {renderIfIndicatorTitle()}
          <h4 className={titleTextClassNameList} title={titleAttr ?? title}>
            {title}
          </h4>
        </header>
        {renderIfValue() /* PriceBox */}
        {renderIfFlexibleButton() /* Flexible Button */}
        {stacked ? renderIfDetails() /* Stacked */ : renderIfChildren() /* IconList */}
        {renderIfDescription()}
        <footer className="c-compare-and-select__footer">
          {renderIfButton()}
          {renderIfCheckbox()}
          {renderIfLegal()}
          {renderIfRadioButtonSection()}
        </footer>
      </div>
    </div>
  )
}

CompareAndSelect.Item = CompareAndSelectItem
CompareAndSelect.Item.displayName = 'CompareAndSelect.Item'

CompareAndSelect.Item.propTypes = {
  ariaLabelDiscountOldPrice: PropTypes.string, // see PriceBox.ariaLabelDiscountOldPrice
  ariaLabelDiscountValue: PropTypes.string, // see PriceBox.ariaLabelDiscountValue
  ariaLabelPrice: PropTypes.string, // see PriceBox.ariaLabelPrice
  buttonLabel: PropTypes.string, // Bronson template: 'has-button'.
  buttonVariant: PropTypes.bool, // Bronson template: 'is-button-variant' from parent.
  checkbox: PropTypes.node, // Bronson template: 'checkbox'.
  children: PropTypes.node, // Bronson template: 'icon-list'. Use 'SelectAndCompare.CompareAndSelect.IconList' component.
  className: PropTypes.string, // additional CSS classes on main element 'c-compare-and-select__item'
  description: PropTypes.node, // Bronson template: 'description'.
  descriptionClassName: PropTypes.string, // adds custom CSS classes to the description element 'c-compare-and-select__description'
  descriptionInfoIcon: PropTypes.element, // Bronson template: 'info-icon'. Use component InfoIcon
  descriptionLinkHref: PropTypes.string, // Bronson template: 'link.href'
  descriptionLinkLabel: PropTypes.string, // Bronson template: 'link.label'
  descriptionTitle: PropTypes.string,
  detailsButtonLabel: PropTypes.string, // Bronson template: 'button'.
  disableButton: PropTypes.bool,
  discountOldPrice: PropTypes.node, // Bronson template: 'pricing.discount.price', see PriceBox.discountOldPrice
  discountValue: PropTypes.node, // Bronson template: 'pricing.discount.value', see PriceBox.discountValue
  flexibleButton: PropTypes.bool, // Bronson template: 'has-flexible-button'.
  icon: PropTypes.string, // Bronson template: 'has-icon'.
  index: PropTypes.number,
  indicatorTitle: PropTypes.string, // Bronson template: 'indicator-title'.
  indicatorTitleAttr: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // Bronson template: 'indicator-title' overwrite, default = null.
  infoIcon: PropTypes.node, // still used for backward compatibility
  // eslint-disable-next-line react/forbid-prop-types
  inputAttributes: PropTypes.oneOfType([PropTypes.object]), // Bronson template: 'input-attribute'.
  label: PropTypes.string, // Bronson template: 'has-label'.
  legal: PropTypes.node,
  legalTitle: PropTypes.string,
  price: PropTypes.node, // Bronson template: 'pricing.price', see PriceBox.price
  pricingAdditionalInfo: PropTypes.node, // Bronson template: 'pricing.additional-info', see PriceBox.additionalInfo
  pricingHeader: PropTypes.node, // Bronson template: 'pricing.header', see PriceBox.header
  radioDescription: PropTypes.node, // Bronson template: 'radio-description'.
  radioInfoIcon: PropTypes.node, // Bronson template: 'info-icon'.
  radioValue: PropTypes.string, // Bronson template: 'radio-value'.
  stacked: PropTypes.bool, // Bronson template: 'is-stacked'.
  title: PropTypes.node, // Bronson template: 'title'.
  titleAttr: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // Bronson template: 'title' overwrite, default = null.
  truncateTitle: PropTypes.bool, // truncates title, default: false
  value: deprecated(
    PropTypes.string,
    'Deprecated, please use "price" instead. Prop will be removed in Bronson-React v6.0.0.'
  ), // old < 7.32.0, mapped to 'price'
  valueSuffix: deprecated(
    PropTypes.node,
    'Deprecated, please use "pricingHeader" instead. Prop will be removed in Bronson-React v6.0.0.'
  ), // old < 7.32.0, mapped to 'pricingHeader'

  /* Convenience props */
  matchBreakpoint: PropTypes.bool, // Convenience prop to handle the `[open]` attribute depending on the breakpoint. Needed for `renderIfDetails()`.
}

/**
 * Bronson IconListItem component (nested).
 * Render a decorative icon for the icon list.
 * @internal
 *
 * @param {string} icon - Name of the icon to render, e.g. `semantic-clock`.
 * @param {string} iconDescription - The icon description.
 * @param {string} [children] - The icon label to display.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The IconListItem component.
 * @constructor
 */
function IconListItem({ icon, iconDescription, children, ...otherProps }) {
  const iClassNameList = classNames('c-icon-list__icon', 'c-icon', {
    [`c-icon--[${icon}]`]: icon,
  }).trim()

  return (
    <li {...otherProps}>
      <i className={iClassNameList} aria-hidden="true" role="img" />
      <span className="u-visually-hidden">{`${iconDescription}: `}</span>
      {children}
    </li>
  )
}

IconListItem.propTypes = {
  icon: PropTypes.string.isRequired, // Bronson template: 'icon'.
  iconDescription: PropTypes.string.isRequired, // Bronson template: 'icon-description'.
  children: PropTypes.node, // Bronson template: 'text'.
}

IconListItem.displayName = 'CompareAndSelect.IconListItem'
CompareAndSelect.IconListItem = IconListItem
